import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, Select, Table, Tag, message } from 'antd';
import Layout from 'components/Layout';
import withGuard from 'components/withGuard';
import { PERMISSIONS } from 'constants/permissions';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { getCampaigns } from 'api/campaigns';
import { getData } from 'utils';
import { captureException } from 'utils/errors';
import { getCampaignErrorMessage } from './errorMessages';
import { formatCampaignDateTime } from './format';
import SuccessFailBadges from './SuccessFailBadges';
import { StyledWrapper } from './styles';

const STATUS_COLORS = {
  draft: 'default',
  scheduled: 'blue',
  sending: 'processing',
  sent: 'green',
  failed: 'red',
};

function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ count: 0 });
  const navigate = useNavigate();
  const pageSize = 10;
  const loadSeq = useRef(0);

  const loadCampaigns = useCallback(() => {
    const seq = ++loadSeq.current;
    setLoading(true);
    getCampaigns({ search, page, page_size: pageSize, status })
      .then((res) => {
        if (seq !== loadSeq.current) return;
        const data = getData(res)?.data || getData(res);
        const results = data?.results ?? [];
        setCampaigns(Array.isArray(results) ? results : []);
        setPagination({ count: data?.count ?? 0 });
        setLoading(false);
      })
      .catch((e) => {
        if (seq !== loadSeq.current) return;
        captureException(e);
        message.error(getCampaignErrorMessage(e, 'Could not load campaigns.'));
        setLoading(false);
      });
  }, [search, page, status]);

  const debouncedSearch = useDebouncedCallback((value) => {
    setSearch(value);
    setPage(1);
  }, 500);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (name, row) => (
        <span
          className="campaign-name-link"
          onClick={() => navigate(`/console/marketing/campaigns/${row.id}`)}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') navigate(`/console/marketing/campaigns/${row.id}`);
          }}
        >
          {name}
        </span>
      ),
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
      width: 88,
      render: (channel) => (channel === 'sms' ? 'SMS' : 'Email'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 108,
      render: (value) => (
        <Tag color={STATUS_COLORS[value] || 'default'}>
          {(value || '').charAt(0).toUpperCase() + (value || '').slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Send at',
      dataIndex: 'send_at',
      key: 'send_at',
      width: 128,
      render: (value) => (
        <span className="value-datetime">{formatCampaignDateTime(value)}</span>
      ),
    },
    {
      title: 'Recipients',
      dataIndex: 'recipient_count',
      key: 'recipient_count',
      width: 100,
    },
    {
      title: 'Results',
      key: 'counts',
      width: 240,
      render: (_, row) => (
        <SuccessFailBadges
          compact
          success={row.success_count}
          failure={row.failure_count}
          skipped={row.skipped_count}
          status={row.status}
        />
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 128,
      render: (value) => (
        <span className="value-datetime">{formatCampaignDateTime(value)}</span>
      ),
    },
  ];

  return (
    <Layout>
      <StyledWrapper>
        <div className="campaigns-header">
          <div>
            <h1 className="campaigns-title">Campaigns</h1>
            <p className="campaigns-subtitle">Send SMS or email to segments and customers</p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/console/marketing/campaigns/create')}
          >
            Create campaign
          </Button>
        </div>

        <div className="filters">
          <Input
            allowClear
            placeholder="Search campaigns"
            prefix={<SearchOutlined />}
            style={{ width: 280 }}
            onChange={(e) => debouncedSearch(e.target.value)}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={status || undefined}
            onChange={(value) => {
              setStatus(value || '');
              setPage(1);
            }}
            options={[
              { value: 'draft', label: 'Draft' },
              { value: 'scheduled', label: 'Scheduled' },
              { value: 'sent', label: 'Sent' },
              { value: 'failed', label: 'Failed' },
            ]}
          />
        </div>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={campaigns}
          tableLayout="fixed"
          pagination={{
            current: page,
            pageSize,
            total: pagination.count,
            onChange: setPage,
          }}
        />
      </StyledWrapper>
    </Layout>
  );
}

export default withGuard(Campaigns, [PERMISSIONS.MARKETING], { redirect: true });
